import axios from 'axios';
import { API_BASE_URL } from '../config/apiConfig';

const API_URL = API_BASE_URL;

// Types
export interface ReportTemplate {
  id: number;
  name: string;
  slug: string;
  type: string;
  description: string;
  data_sources: any[];
  filters: any[];
  visualizations: any[];
  template_content: string | null;
  is_system: boolean;
  is_active: boolean;
  created_by: number;
  creator_name: string;
  reports_count: number;
  created_at: string;
  updated_at: string;
}

export interface Report {
  id: number;
  template_id: number;
  project_id: number | null;
  created_by: number;
  title: string;
  description: string | null;
  type: string;
  status: 'pending' | 'generating' | 'ready' | 'error';
  format: 'pdf' | 'excel' | 'csv' | 'html';
  generated_at: string | null;
  generation_duration_seconds: number | null;
  file_path: string | null;
  file_size: string | null;
  file_size_bytes: number | null;
  filters: any;
  parameters: any;
  report_data: any;
  error_message: string | null;
  recipients: string[];
  is_public: boolean;
  share_token: string;
  download_count: number;
  last_downloaded_at: string | null;
  view_count: number;
  last_viewed_at: string | null;
  creator_name: string;
  project_name: string | null;
  is_ready: boolean;
  age_in_days: number;
  template?: ReportTemplate;
  created_at: string;
  updated_at: string;
}

export interface ReportSchedule {
  id: number;
  template_id: number;
  project_id: number | null;
  created_by: number;
  name: string;
  description: string | null;
  frequency: 'daily' | 'weekly' | 'monthly' | 'quarterly';
  day_of_week: number | null;
  day_of_month: number | null;
  time_of_day: string;
  format: 'pdf' | 'excel' | 'csv' | 'html';
  recipients: string[];
  filters: any;
  parameters: any;
  is_active: boolean;
  next_run_at: string;
  last_run_at: string | null;
  last_run_status: string | null;
  run_count: number;
  success_count: number;
  failure_count: number;
  consecutive_failures: number;
  error_message: string | null;
  creator_name: string;
  project_name: string | null;
  is_due: boolean;
  success_rate: number | null;
  template?: ReportTemplate;
  created_at: string;
  updated_at: string;
}

export interface ReportStatistics {
  reports: {
    total: number;
    ready: number;
    pending: number;
    error: number;
    recent_30_days: number;
  };
  engagement: {
    total_downloads: number;
    total_views: number;
    avg_downloads_per_report: number;
  };
  distribution: {
    by_type: Record<string, number>;
    by_format: Record<string, number>;
  };
  schedules: {
    active: number;
    due: number;
  };
}

export interface CreateReportRequest {
  template_id: number;
  project_id?: number;
  title: string;
  description?: string;
  format: 'pdf' | 'excel' | 'csv' | 'html';
  filters?: any;
  parameters?: any;
  recipients?: string[];
  is_public?: boolean;
}

export interface UpdateReportRequest {
  title?: string;
  description?: string;
  recipients?: string[];
  is_public?: boolean;
}

export interface CreateScheduleRequest {
  template_id: number;
  project_id?: number;
  name: string;
  description?: string;
  frequency: 'daily' | 'weekly' | 'monthly' | 'quarterly';
  day_of_week?: number;
  day_of_month?: number;
  time_of_day: string;
  format: 'pdf' | 'excel' | 'csv' | 'html';
  recipients?: string[];
  filters?: any;
  parameters?: any;
}

export interface UpdateScheduleRequest {
  name?: string;
  description?: string;
  frequency?: 'daily' | 'weekly' | 'monthly' | 'quarterly';
  day_of_week?: number;
  day_of_month?: number;
  time_of_day?: string;
  format?: 'pdf' | 'excel' | 'csv' | 'html';
  recipients?: string[];
  filters?: any;
  parameters?: any;
  is_active?: boolean;
}

class ReportsService {
  private getAuthHeaders() {
    const token = localStorage.getItem('auth_token');
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  // Report Templates
  async getTemplates(): Promise<ReportTemplate[]> {
    const response = await axios.get(
      `${API_URL}/project-manager/reports/templates/list`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  // Reports
  async getReports(filters?: {
    project_id?: number;
    type?: string;
    status?: string;
    format?: string;
    created_by?: number;
    from_date?: string;
    to_date?: string;
  }): Promise<Report[]> {
    const response = await axios.get(
      `${API_URL}/project-manager/reports`,
      {
        headers: this.getAuthHeaders(),
        params: filters,
      }
    );
    return response.data;
  }

  async getReport(id: number): Promise<Report> {
    const response = await axios.get(
      `${API_URL}/project-manager/reports/${id}`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async createReport(data: CreateReportRequest): Promise<{ message: string; report: Report }> {
    const response = await axios.post(
      `${API_URL}/project-manager/reports`,
      data,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async updateReport(id: number, data: UpdateReportRequest): Promise<{ message: string; report: Report }> {
    const response = await axios.put(
      `${API_URL}/project-manager/reports/${id}`,
      data,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async deleteReport(id: number): Promise<{ message: string }> {
    const response = await axios.delete(
      `${API_URL}/project-manager/reports/${id}`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async downloadReport(id: number): Promise<void> {
    const response = await axios.get(
      `${API_URL}/project-manager/reports/${id}/download`,
      {
        headers: this.getAuthHeaders(),
        responseType: 'blob',
      }
    );

    // Create a download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    
    // Extract filename from Content-Disposition header if available
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'report';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  async shareReport(id: number): Promise<{ message: string; share_url: string; share_token: string }> {
    const response = await axios.post(
      `${API_URL}/project-manager/reports/${id}/share`,
      {},
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  // Schedules
  async getSchedules(filters?: {
    project_id?: number;
    is_active?: boolean;
  }): Promise<ReportSchedule[]> {
    const response = await axios.get(
      `${API_URL}/project-manager/reports/schedules`,
      {
        headers: this.getAuthHeaders(),
        params: filters,
      }
    );
    return response.data;
  }

  async createSchedule(data: CreateScheduleRequest): Promise<{ message: string; schedule: ReportSchedule }> {
    const response = await axios.post(
      `${API_URL}/project-manager/reports/schedules`,
      data,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async updateSchedule(id: number, data: UpdateScheduleRequest): Promise<{ message: string; schedule: ReportSchedule }> {
    const response = await axios.put(
      `${API_URL}/project-manager/reports/schedules/${id}`,
      data,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async deleteSchedule(id: number): Promise<{ message: string }> {
    const response = await axios.delete(
      `${API_URL}/project-manager/reports/schedules/${id}`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  // Statistics
  async getStatistics(projectId?: number): Promise<ReportStatistics> {
    const response = await axios.get(
      `${API_URL}/project-manager/reports/statistics`,
      {
        headers: this.getAuthHeaders(),
        params: projectId ? { project_id: projectId } : undefined,
      }
    );
    return response.data;
  }
}

export default new ReportsService();
